import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { getProducts } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const gbp = (n) => `£${n.toFixed(2)}`;

const NumberedCard = ({ product, index }) => {
  const { addItem } = useCart();
  const n = String(index + 1).padStart(2, "0");

  const quickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!addItem(product, "product", 1)) return;
    toast.success(`${product.name} added to cart`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
    >
      <Link to={`/product/${product.id}`} data-testid="product-card" className="group relative block h-full overflow-hidden rounded-2xl glass metallic-border card-hover">
        <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
          <img src={product.image} alt={product.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E]/85 via-transparent to-transparent" />
          <span className="absolute left-5 top-4 font-display text-3xl font-bold text-white/40 group-hover:text-white/60 transition-colors">{n}</span>
          <button onClick={quickAdd} data-testid="product-add-to-cart-btn" aria-label="Add to cart" className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full btn-metallic opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            <Plus className="h-5 w-5" />
          </button>
          <div className="absolute bottom-4 left-5 right-5">
            <span className="font-mono2 text-[10px] uppercase tracking-widest text-slate-200">{product.category}</span>
            <h3 className="font-display text-lg font-semibold text-white">{product.name}</h3>
          </div>
        </div>
        <div className="p-5">
          <p className="line-clamp-2 text-sm leading-relaxed text-slate-600">{product.overview}</p>
          <div className="mt-4 flex items-center justify-between">
            <span className="text-lg font-semibold metallic-text">{gbp(product.price)}</span>
            <span className="inline-flex items-center gap-1.5 text-sm text-slate-700 group-hover:text-slate-900">View Details <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export const ProductsShowcase = () => {
  const [products, setProducts] = useState([]);
  useEffect(() => { getProducts().then(setProducts).catch(() => {}); }, []);

  return (
    <section id="research" className="relative py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="max-w-2xl">
          <span className="font-mono2 text-xs uppercase tracking-[0.3em] text-slate-500">What we offer</span>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">Research peptides, done properly.</h2>
          <p className="mt-4 text-lg leading-relaxed text-slate-600">High-quality research peptides — one UK team, precise specifications, and testing documentation where available.</p>
        </motion.div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p, i) => <NumberedCard key={p.id} product={p} index={i} />)}
        </div>

        <div className="mt-12 flex justify-center">
          <Link to="/shop"><Button variant="outline" className="h-11 border-slate-200 bg-transparent px-6 text-slate-700 hover:bg-slate-100 hover:text-slate-900">View full catalogue <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
        </div>
      </div>
    </section>
  );
};

export default ProductsShowcase;
